import { APPLE_PAY_API_VERSION } from '@monofront/business/constants';
import { useCallback, useRef } from 'react';
import useApplePay from './useApplePay';
import { useTranslations } from 'next-intl';
import { useCheckoutPayment } from '../contexts/CheckoutPaymentContext';
import useHandlePaymentError from './useHandlePaymentErrors';

interface IUseApplePaySessionProps {
  onPaymentAuthorized: (
    token: ApplePayJS.ApplePayPaymentToken,
  ) => Promise<boolean>;
  paymentRequestPayload: ApplePayJS.ApplePayPaymentRequest;
  onSessionCancel?: () => void;
}

/**
 * APPLE PAY SESSION LIFECYCLE:
 * - Session created → onvalidatemerchant called → onpaymentauthorized called → Session completed
 */

const useApplePaySession = ({
  onPaymentAuthorized,
  paymentRequestPayload,
  onSessionCancel,
}: IUseApplePaySessionProps) => {
  const { validateApplePayMerchant } = useApplePay();
  const tErrors = useTranslations('Errors');
  const { setIsOrderValidationInProgress } = useCheckoutPayment();
  const { handlePaymentError } = useHandlePaymentError();
  const sessionRef = useRef<ApplePaySession | null>(null);

  const createSession = useCallback(async () => {
    try {
      setIsOrderValidationInProgress(true);

      // Step 1: Create payment request payload and Apple Pay session
      // paymentRequestPayload is the payment request object with amount, currency, etc.
      const paymentSession = new ApplePaySession(
        APPLE_PAY_API_VERSION,
        paymentRequestPayload,
      );

      // Step 2: Set up merchant validation handler
      // This validates that our domain is authorized to use Apple Pay
      // Apple calls this first to ensure we're a legitimate merchant
      paymentSession.onvalidatemerchant = async (event) => {
        await validateApplePayMerchant(paymentSession, event.validationURL);
      };

      // Step 3: Set up payment authorization handler
      // This is called when user authorizes payment in Apple Pay
      // We receive the OPAQUE TOKEN here - encrypted payment data we can't read
      // We must send this token to our backend to decrypt and process
      paymentSession.onpaymentauthorized = async (event) => {
        const isActionCompleted = await onPaymentAuthorized(
          event.payment.token,
        );

        if (isActionCompleted) {
          paymentSession.completePayment({
            status: ApplePaySession.STATUS_SUCCESS,
          });
        } else {
          paymentSession.completePayment({
            status: ApplePaySession.STATUS_FAILURE,
            errors: [
              {
                code: 'unknown',
                message: tErrors('paymentAuthorizationFailed'),
              },
            ],
          });
        }
      };

      paymentSession.oncancel = () => {
        setIsOrderValidationInProgress(false);
        onSessionCancel?.();
      };

      // Step 5: Begin the Apple Pay session
      // This will show the Apple Pay UI to the user
      // Apple Pay takes over the user experience from here
      // We just respond to the events they send us
      sessionRef.current = paymentSession;
      paymentSession.begin();
    } catch (error) {
      setIsOrderValidationInProgress(false);
      handlePaymentError({
        code: 'PAY_WITH_NEW_APPLEPAY_FAILED',
        message: 'Apple pay payment request failed or was cancelled',
        error,
      });
    }
  }, [
    paymentRequestPayload,
    validateApplePayMerchant,
    onPaymentAuthorized,
    setIsOrderValidationInProgress,
    handlePaymentError,
    onSessionCancel,
  ]);

  return {
    createSession,
  };
};

export default useApplePaySession;
